import { Injectable } from '@angular/core';

export interface FormatResult {
    score: string | null;
    result: string | null; // Per-phase scores (renamed from 'details')
    goals?: Array<{ minute: number; team: string; phase?: string }> | null; // Football only
}

export enum Sport {
    FOOTBALL = 'F',
    BASKETBALL = 'B',
    TENNIS = 'T',
    VOLLEYBALL = 'V',
    HANDBALL = 'H',
    ICE_HOCKEY = 'IH',
    TABLE_TENNIS = 'TT',
    MMA = 'MMA',
    DARTS = 'DARTS',
    BADMINTON = 'BADMINTON',
    BASEBALL = 'BASEBALL',
    AMERICAN_FOOTBALL = 'AMERICAN_FOOTBALL',
    SNOOKER = 'SNOOKER',
    RUGBY = 'RUGBY',
    RUGBY_UNION = 'RUGBY_UNION',
    BOXING = 'BOXING',
    WATERPOLO = 'WATERPOLO',
    FUTSAL = 'FUTSAL',
    ESPORTS = 'ESPORTS',
    CRICKET = 'CRICKET',
    BASKETBALL_3X3 = 'BASKETBALL_3X3',
    BASKETBALL_2X20 = 'BASKETBALL_2X20',
    UNKNOWN = ''
}

/**
 * Sport feature configuration based on display format requirements
 */
export const SPORT_FEATURES = {
    FULL_DETAIL_DISPLAY: [Sport.FOOTBALL],

    SIMULATION_DISPLAY: [
        Sport.BASKETBALL,
        Sport.TENNIS,
        Sport.ICE_HOCKEY,
        Sport.HANDBALL,
        Sport.RUGBY,
        Sport.RUGBY_UNION,
        Sport.AMERICAN_FOOTBALL,
        Sport.VOLLEYBALL,
        Sport.BADMINTON,
        Sport.WATERPOLO,
        Sport.FUTSAL
    ],
    WINNER_ONLY_DISPLAY: [
        Sport.BASEBALL,
        Sport.TABLE_TENNIS,
        Sport.DARTS,
        Sport.MMA,
        Sport.BOXING,
        Sport.ESPORTS,
        Sport.SNOOKER,
        Sport.CRICKET,
        Sport.BASKETBALL_3X3,
        Sport.BASKETBALL_2X20
    ],
    OVERTIME_SUPPORTED: [
        Sport.BASKETBALL,
        Sport.ICE_HOCKEY,
        Sport.HANDBALL,
        Sport.RUGBY,
        Sport.RUGBY_UNION,
        Sport.AMERICAN_FOOTBALL
    ]
};

@Injectable({
    providedIn: 'root'
})
export class SportResultFormatterService {
    constructor() {}

    /**
     * Check if sport uses full detail display format (Football only)
     */
    usesFullDetailDisplay(sport: Sport): boolean {
        return SPORT_FEATURES.FULL_DETAIL_DISPLAY.includes(sport);
    }

    /**
     * Check if sport uses simulation display format
     */
    usesSimulationDisplay(sport: Sport): boolean {
        return SPORT_FEATURES.SIMULATION_DISPLAY.includes(sport);
    }

    /**
     * Check if sport uses winner-only display format
     */
    usesWinnerOnlyDisplay(sport: Sport): boolean {
        return SPORT_FEATURES.WINNER_ONLY_DISPLAY.includes(sport);
    }

    /**
     * Check if sport supports overtime
     */
    supportsOvertime(sport: Sport): boolean {
        return SPORT_FEATURES.OVERTIME_SUPPORTED.includes(sport);
    }

    /**
     * Main method for formatting simulation results based on sport
     * - Winner-only sports: Only score field (1:0 or 0:1)
     * - Other sports: score + result (per-phase breakdown)
     * - Football: score + result + goals (goal events)
     */
    formatResult(simulation: any, includeGoals: boolean = false): FormatResult {
        if (!simulation) {
            return { score: null, result: null, goals: null };
        }

        const sport = this.normalizeSportCode(simulation.S);

        // Check if this sport uses winner-only display format
        if (this.usesWinnerOnlyDisplay(sport)) {
            return {
                score: `${simulation.score.winner.home}:${simulation.score.winner.away}`,
                result: null,
                goals: null
            };
        }

        const scoreData = simulation.score || {};
        const resultData = simulation.result || {};

        try {
            const goalEvents = Array.isArray(simulation.details)
                ? simulation.details.filter((d: any) => d.type === 'goal')
                : [];

            switch (sport) {
                case Sport.FOOTBALL:
                    return this.formatFootballResult(scoreData, resultData, {
                        goalEvents: goalEvents.length ? goalEvents : undefined,
                        includeGoals
                    });
                case Sport.BASKETBALL:
                    return this.formatBasketballResult(scoreData, resultData);
                case Sport.TENNIS:
                    return this.formatTennisResult(scoreData, resultData);
                case Sport.VOLLEYBALL:
                    return this.formatVolleyballResult(scoreData, resultData);
                case Sport.HANDBALL:
                    return this.formatHandballResult(scoreData, resultData);
                case Sport.ICE_HOCKEY:
                    return this.formatIceHockeyResult(scoreData, resultData);
                case Sport.TABLE_TENNIS:
                    return this.formatTableTennisResult(scoreData, resultData);
                case Sport.MMA:
                    return this.formatMMAResult(scoreData, resultData);
                case Sport.DARTS:
                    return this.formatDartsResult(scoreData, resultData);
                case Sport.BADMINTON:
                    return this.formatBadmintonResult(scoreData, resultData);
                case Sport.BASEBALL:
                    return this.formatBaseballResult(scoreData, resultData);
                case Sport.AMERICAN_FOOTBALL:
                    return this.formatAmericanFootballResult(
                        scoreData,
                        resultData
                    );
                case Sport.SNOOKER:
                    return this.formatSnookerResult(scoreData, resultData);
                case Sport.RUGBY:
                case Sport.RUGBY_UNION:
                    return this.formatRugbyResult(scoreData, resultData);
                case Sport.BOXING:
                    return this.formatBoxingResult(scoreData, resultData);
                case Sport.WATERPOLO:
                    return this.formatWaterpoloResult(scoreData, resultData);
                case Sport.FUTSAL:
                    return this.formatFutsalResult(scoreData, resultData);
                case Sport.ESPORTS:
                    return this.formatEsportsResult(scoreData, resultData);
                case Sport.CRICKET:
                    return this.formatCricketResult(scoreData, resultData);
                case Sport.BASKETBALL_3X3:
                case Sport.BASKETBALL_2X20:
                    // Basketball variants use same formatting as regular basketball
                    return this.formatBasketballResult(scoreData, resultData);
                default:
                    return this.formatGenericResult(scoreData, resultData);
            }
        } catch (error) {
            console.error('Error formatting sport result:', error, simulation);
            return { score: null, result: null, goals: null };
        }
    }

    /**
     * Format football result with time markers for goals
     * Score: "2 : 1" (final score)
     * Result: "1:1 | 1:0" (per-half breakdown)
     * Goals: Array of goal events with minute, team, phase (football only)
     */
    private formatFootballResult(
        scoreData: any,
        resultData: any,
        extra?: {
            goalEvents?: Array<{
                minute: number;
                team: 'home' | 'away';
                phase?: '1st_half' | '2nd_half' | string;
                cumulativeScore?: { home: number; away: number };
                type?: string;
            }>;
            includeGoals?: boolean;
        }
    ): FormatResult {
        let score: string | null = null;
        if (scoreData?.goals) {
            score = `${scoreData.goals.home ?? 0} : ${scoreData.goals.away ?? 0}`;
        } else if (scoreData?.fullTime || scoreData?.ft) {
            const ft = scoreData.fullTime || scoreData.ft;
            score = `${ft?.home ?? 0} : ${ft?.away ?? 0}`;
        } else if (
            scoreData?.home !== undefined &&
            scoreData?.away !== undefined
        ) {
            score = `${scoreData.home} : ${scoreData.away}`;
        }

        const fhGoals = resultData?.['1st_half']?.goals;
        const shGoals = resultData?.['2nd_half']?.goals;
        const fhStr = fhGoals
            ? `${fhGoals.home ?? 0}:${fhGoals.away ?? 0}`
            : '-:-';
        const shStr = shGoals
            ? `${shGoals.home ?? 0}:${shGoals.away ?? 0}`
            : '-:-';

        // Ako ukupni rezultat nedostaje, izvedi iz poluvremena kada je moguce
        if (!score && (fhStr !== '-:-' || shStr !== '-:-')) {
            const [fhH, fhA] = fhStr
                .split(':')
                .map((n) => parseInt(n, 10) || 0);
            if (shStr !== '-:-') {
                const [shH, shA] = shStr
                    .split(':')
                    .map((n) => parseInt(n, 10) || 0);
                score = `${fhH + shH} : ${fhA + shA}`;
            } else {
                score = `${fhH} : ${fhA}`;
            }
        }

        const events: any[] =
            extra?.goalEvents ??
            resultData?.goalEvents ??
            (Array.isArray(resultData?.details) ? resultData.details : []) ??
            [];

        const phaseTag = (p?: string) =>
            p === '1st_half'
                ? '[1H]'
                : p === '2nd_half'
                  ? '[2H]'
                  : p
                    ? `[${p}]`
                    : '';

        const teamTag = (t?: string) =>
            t === 'home' ? 'H' : t === 'away' ? 'A' : (t ?? '?');

        const sortedEvents = Array.isArray(events)
            ? [...events].sort((a, b) => (a?.minute ?? 0) - (b?.minute ?? 0))
            : [];

        // Result: per-half breakdown
        const result = `${fhStr} | ${shStr}`;

        // Goals: only for football, contains goal events
        let goals: Array<{
            minute: number;
            team: string;
            phase?: string;
        }> | null = null;

        if (extra?.includeGoals && sortedEvents.length > 0) {
            goals = sortedEvents.map((ev) => ({
                minute: ev?.minute ?? 0,
                team: teamTag(ev?.team),
                phase: phaseTag(ev?.phase)
            }));
        }

        return { score, result, goals };
    }

    /**
     * Format basketball result
     * Score: "85 : 78" (total points)
     * Result: "20:18 | 22:20 | 21:19 | 22:21 | OT 5:3" (quarters + overtime)
     */
    private formatBasketballResult(
        scoreData: any,
        resultData: any
    ): FormatResult {
        let score = null;
        let details = null;

        // Izvuci finalni rezultat
        if (scoreData?.points) {
            score = `${scoreData.points.home || 0} : ${scoreData.points.away || 0}`;
        } else if (scoreData?.final) {
            score = `${scoreData.final.home || 0} : ${scoreData.final.away || 0}`;
        } else if (
            scoreData?.home !== undefined &&
            scoreData?.away !== undefined
        ) {
            score = `${scoreData.home} : ${scoreData.away}`;
        }

        if (resultData) {
            const quarterResults: string[] = [];
            let totalHome = 0;
            let totalAway = 0;

            ['quarter1', 'quarter2', 'quarter3', 'quarter4'].forEach(
                (quarter) => {
                    const q = resultData[quarter];
                    if (q?.points) {
                        quarterResults.push(
                            `${q.points.home || 0}:${q.points.away || 0}`
                        );
                        totalHome += q.points.home || 0;
                        totalAway += q.points.away || 0;
                    } else if (
                        q &&
                        q.home !== undefined &&
                        q.away !== undefined
                    ) {
                        quarterResults.push(`${q.home}:${q.away}`);
                        totalHome += q.home || 0;
                        totalAway += q.away || 0;
                    } else {
                        quarterResults.push('-:-');
                    }
                }
            );

            if (
                quarterResults.every((q) => q === '-:-') &&
                resultData.S &&
                Array.isArray(resultData.S)
            ) {
                quarterResults.length = 0;
                totalHome = 0;
                totalAway = 0;

                resultData.S.forEach((quarterData: any) => {
                    if (quarterData && quarterData.P) {
                        const [homeScore, awayScore] =
                            quarterData.P.split('-').map(Number);
                        if (!isNaN(homeScore) && !isNaN(awayScore)) {
                            quarterResults.push(`${homeScore}:${awayScore}`);
                            totalHome += homeScore;
                            totalAway += awayScore;
                        }
                    }
                });
            }

            // Obradi produzetak
            const overtime = resultData.overtime;
            if (overtime?.points) {
                quarterResults.push(
                    `OT ${overtime.points.home || 0}:${overtime.points.away || 0}`
                );
                totalHome += overtime.points.home || 0;
                totalAway += overtime.points.away || 0;
            } else if (
                overtime &&
                overtime.home !== undefined &&
                overtime.away !== undefined
            ) {
                quarterResults.push(`OT ${overtime.home}:${overtime.away}`);
                totalHome += overtime.home || 0;
                totalAway += overtime.away || 0;
            }

            if (
                quarterResults.length > 0 &&
                !quarterResults.every((q) => q === '-:-')
            ) {
                details = quarterResults.join(' | ');

                if ((!score && totalHome > 0) || totalAway > 0) {
                    score = `${totalHome} : ${totalAway}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format tennis result
     * Score: "2 : 1" (sets won)
     * Result: "6:4 | 7:5 | 6:3" (games per set)
     */
    private formatTennisResult(scoreData: any, resultData: any): FormatResult {
        let score = null;
        let details = null;

        if (scoreData?.sets) {
            score = `${scoreData.sets.home || 0} : ${scoreData.sets.away || 0}`;
        }

        if (resultData) {
            const setResults: string[] = [];
            let homeSets = 0;
            let awaySets = 0;

            for (let i = 1; i <= 5; i++) {
                const setKey = `set${i}`;
                const set = resultData[setKey];

                if (set?.games) {
                    const homeGames = set.games.home || 0;
                    const awayGames = set.games.away || 0;
                    setResults.push(`${homeGames}:${awayGames}`);

                    if (homeGames > awayGames) {
                        homeSets++;
                    } else if (awayGames > homeGames) {
                        awaySets++;
                    }
                } else if (
                    set &&
                    set.home !== undefined &&
                    set.away !== undefined
                ) {
                    setResults.push(`${set.home}:${set.away}`);

                    if (set.home > set.away) {
                        homeSets++;
                    } else if (set.away > set.home) {
                        awaySets++;
                    }
                }
            }

            if (setResults.length > 0) {
                details = setResults.join(' | ');

                if (!score) {
                    score = `${homeSets} : ${awaySets}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format volleyball result
     * Score: "3 : 1" (sets won)
     * Result: "25:20 | 23:25 | 25:18 | 25:22" (points per set)
     */
    private formatVolleyballResult(
        scoreData: any,
        resultData: any
    ): FormatResult {
        let score = null;
        let details = null;

        if (scoreData?.sets) {
            score = `${scoreData.sets.home || 0} : ${scoreData.sets.away || 0}`;
        }

        if (resultData) {
            const setResults: string[] = [];
            let homeSets = 0;
            let awaySets = 0;

            for (let i = 1; i <= 5; i++) {
                const setKey = `set${i}`; // Fixed: Changed from s${i} to set${i} to match settlement service
                const set = resultData[setKey];

                if (set?.points) {
                    const homePoints = set.points.home || 0;
                    const awayPoints = set.points.away || 0;
                    setResults.push(`${homePoints}:${awayPoints}`);

                    if (homePoints > awayPoints) {
                        homeSets++;
                    } else if (awayPoints > homePoints) {
                        awaySets++;
                    }
                } else if (
                    set &&
                    set.home !== undefined &&
                    set.away !== undefined
                ) {
                    setResults.push(`${set.home}:${set.away}`);

                    if (set.home > set.away) {
                        homeSets++;
                    } else if (set.away > set.home) {
                        awaySets++;
                    }
                }
            }

            if (setResults.length > 0) {
                details = setResults.join(' | ');

                if (!score) {
                    score = `${homeSets} : ${awaySets}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format badminton result
     * Score: "2 : 1" (sets won)
     * Result: "21:18 | 19:21 | 21:15" (points per set)
     */
    private formatBadmintonResult(
        scoreData: any,
        resultData: any
    ): FormatResult {
        let score = null;
        let details = null;

        if (scoreData?.sets) {
            score = `${scoreData.sets.home || 0} : ${scoreData.sets.away || 0}`;
        }

        if (resultData) {
            const setResults: string[] = [];
            let homeSets = 0;
            let awaySets = 0;

            for (let i = 1; i <= 5; i++) {
                const setKey = `set${i}`;
                const set = resultData[setKey];

                if (set?.points) {
                    const homePoints = set.points.home || 0;
                    const awayPoints = set.points.away || 0;
                    setResults.push(`${homePoints}:${awayPoints}`);

                    if (homePoints > awayPoints) {
                        homeSets++;
                    } else if (awayPoints > homePoints) {
                        awaySets++;
                    }
                } else if (
                    set &&
                    set.home !== undefined &&
                    set.away !== undefined
                ) {
                    setResults.push(`${set.home}:${set.away}`);

                    if (set.home > set.away) {
                        homeSets++;
                    } else if (set.away > set.home) {
                        awaySets++;
                    }
                }
            }

            if (setResults.length > 0) {
                details = setResults.join(' | ');

                if (!score) {
                    score = `${homeSets} : ${awaySets}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format handball result
     * Score: "28 : 24" (total goals)
     * Result: "14:12 | 14:12 | OT 2:1" (per-half breakdown + optional overtime)
     */
    private formatHandballResult(
        scoreData: any,
        resultData: any
    ): FormatResult {
        let score = null;
        let details = null;

        // Extract main score
        if (scoreData?.goals) {
            score = `${scoreData.goals.home || 0} : ${scoreData.goals.away || 0}`;
        } else if (
            scoreData?.home !== undefined &&
            scoreData?.away !== undefined
        ) {
            score = `${scoreData.home} : ${scoreData.away}`;
        }

        if (resultData) {
            const halfResults: string[] = [];
            let totalHome = 0;
            let totalAway = 0;

            // Process halves
            ['1st_half', '2nd_half'].forEach((half) => {
                const h = resultData[half];
                if (h?.goals) {
                    halfResults.push(
                        `${h.goals.home || 0}:${h.goals.away || 0}`
                    );
                    totalHome += h.goals.home || 0;
                    totalAway += h.goals.away || 0;
                } else if (h && h.home !== undefined && h.away !== undefined) {
                    halfResults.push(`${h.home}:${h.away}`);
                    totalHome += h.home || 0;
                    totalAway += h.away || 0;
                } else {
                    halfResults.push('-:-');
                }
            });

            // Process overtime if present
            const overtime = resultData.overtime;
            if (overtime?.goals) {
                halfResults.push(
                    `OT ${overtime.goals.home || 0}:${overtime.goals.away || 0}`
                );
                totalHome += overtime.goals.home || 0;
                totalAway += overtime.goals.away || 0;
            } else if (
                overtime &&
                overtime.home !== undefined &&
                overtime.away !== undefined
            ) {
                halfResults.push(`OT ${overtime.home}:${overtime.away}`);
                totalHome += overtime.home || 0;
                totalAway += overtime.away || 0;
            }

            if (
                halfResults.length > 0 &&
                !halfResults.every((h) => h === '-:-')
            ) {
                details = halfResults.join(' | ');

                if (!score && (totalHome > 0 || totalAway > 0)) {
                    score = `${totalHome} : ${totalAway}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format table tennis result
     * Score: "3 : 1" (sets won)
     * Result: "11:8 | 11:9 | 11:7 | 11:10" (points per set)
     */
    private formatTableTennisResult(
        scoreData: any,
        resultData: any
    ): FormatResult {
        let score = null;
        let details = null;

        // Izvuci rezultat setova - Settlement uses points indicator
        if (scoreData?.sets) {
            score = `${scoreData.sets.home || 0} : ${scoreData.sets.away || 0}`;
        }

        if (resultData) {
            const setResults: string[] = [];
            let homeSets = 0;
            let awaySets = 0;

            for (let i = 1; i <= 5; i++) {
                const setKey = `set${i}`;
                const set = resultData[setKey];

                if (set?.points) {
                    const homePoints = set.points.home || 0;
                    const awayPoints = set.points.away || 0;
                    setResults.push(`${homePoints}:${awayPoints}`);

                    if (homePoints > awayPoints) {
                        homeSets++;
                    } else if (awayPoints > homePoints) {
                        awaySets++;
                    }
                } else if (
                    set &&
                    set.home !== undefined &&
                    set.away !== undefined
                ) {
                    setResults.push(`${set.home}:${set.away}`);

                    if (set.home > set.away) {
                        homeSets++;
                    } else if (set.away > set.home) {
                        awaySets++;
                    }
                }
            }

            if (setResults.length > 0) {
                details = setResults.join(' | ');

                if (!score) {
                    score = `${homeSets} : ${awaySets}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format ice hockey result
     * Score: "4 : 2" (total goals)
     * Result: "1:0 | 2:1 | 1:1" (per-period breakdown)
     */
    private formatIceHockeyResult(
        scoreData: any,
        resultData: any
    ): FormatResult {
        let score = null;
        let details = null;

        // Izvuci glavni rezultat
        if (scoreData?.goals) {
            score = `${scoreData.goals.home || 0} : ${scoreData.goals.away || 0}`;
        } else if (
            scoreData?.home !== undefined &&
            scoreData?.away !== undefined
        ) {
            score = `${scoreData.home} : ${scoreData.away}`;
        }

        if (resultData) {
            const periodResults: string[] = [];
            let totalHome = 0;
            let totalAway = 0;

            ['period1', 'period2', 'period3'].forEach((period) => {
                const p = resultData[period];
                if (p?.goals) {
                    periodResults.push(
                        `${p.goals.home || 0}:${p.goals.away || 0}`
                    );
                    totalHome += p.goals.home || 0;
                    totalAway += p.goals.away || 0;
                } else if (p && p.home !== undefined && p.away !== undefined) {
                    periodResults.push(`${p.home}:${p.away}`);
                    totalHome += p.home || 0;
                    totalAway += p.away || 0;
                } else {
                    periodResults.push('-:-');
                }
            });

            const overtime = resultData.overtime;
            if (overtime?.goals) {
                periodResults.push(
                    `OT ${overtime.goals.home || 0}:${overtime.goals.away || 0}`
                );
                totalHome += overtime.goals.home || 0;
                totalAway += overtime.goals.away || 0;
            } else if (
                overtime &&
                overtime.home !== undefined &&
                overtime.away !== undefined
            ) {
                periodResults.push(`OT ${overtime.home}:${overtime.away}`);
                totalHome += overtime.home || 0;
                totalAway += overtime.away || 0;
            }

            if (
                periodResults.length > 0 &&
                !periodResults.every((p) => p === '-:-')
            ) {
                details = periodResults.join(' | ');

                if (!score && (totalHome > 0 || totalAway > 0)) {
                    score = `${totalHome} : ${totalAway}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format darts result
     * Score: "6 : 5" (legs won)
     * Result: "32:0 | 0:180 | 0:206 | ..." (points per leg)
     */
    private formatDartsResult(scoreData: any, resultData: any): FormatResult {
        let score = null;
        let details = null;

        if (scoreData?.legs) {
            score = `${scoreData.legs.home || 0} : ${scoreData.legs.away || 0}`;
        }

        if (resultData) {
            const legResults: string[] = [];
            let homeLegs = 0;
            let awayLegs = 0;

            // Settlement service uses leg1-13 phase names
            for (let i = 1; i <= 13; i++) {
                const legKey = `leg${i}`;
                const leg = resultData[legKey];

                if (leg?.points) {
                    const homePoints = leg.points.home || 0;
                    const awayPoints = leg.points.away || 0;
                    legResults.push(`${homePoints}:${awayPoints}`);

                    // Leg winner determination
                    if (homePoints > awayPoints) {
                        homeLegs++;
                    } else if (awayPoints > homePoints) {
                        awayLegs++;
                    }
                }
            }

            if (legResults.length > 0) {
                details = legResults.join(' | ');

                if (!score) {
                    score = `${homeLegs} : ${awayLegs}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Generic result formatting for unknown sports
     */
    private formatGenericResult(scoreData: any, resultData: any): FormatResult {
        let score = null;
        let result = null;

        if (scoreData?.home !== undefined && scoreData?.away !== undefined) {
            score = `${scoreData.home} : ${scoreData.away}`;
        }

        if (resultData && typeof resultData === 'object') {
            try {
                result = JSON.stringify(resultData);
            } catch {
                result = null;
            }
        }

        return { score, result, goals: null };
    }

    /**
     * Format MMA result (winner-only)
     */
    private formatMMAResult(scoreData: any, resultData: any): FormatResult {
        let score = null;

        if (scoreData?.winner) {
            score = `${scoreData.winner.home} : ${scoreData.winner.away}`;
        }

        return { score, result: null, goals: null };
    }

    /**
     * Format baseball result
     * Score: "5 : 3" (total runs)
     * Result: "1:0 | 0:2 | 1:0 | ..." (runs per inning)
     */
    private formatBaseballResult(
        scoreData: any,
        resultData: any
    ): FormatResult {
        let score = null;
        let details = null;

        // Settlement service uses 'points' indicator, not 'runs'
        if (scoreData?.points) {
            score = `${scoreData.points.home || 0} : ${scoreData.points.away || 0}`;
        } else if (scoreData?.runs) {
            // Fallback for legacy data
            score = `${scoreData.runs.home || 0} : ${scoreData.runs.away || 0}`;
        } else if (
            scoreData?.home !== undefined &&
            scoreData?.away !== undefined
        ) {
            score = `${scoreData.home} : ${scoreData.away}`;
        }

        if (resultData) {
            const inningResults: string[] = [];
            let totalHome = 0;
            let totalAway = 0;

            for (let i = 1; i <= 9; i++) {
                const inningKey = `inning${i}`;
                const inning = resultData[inningKey];

                // Settlement service uses 'points' per inning
                if (inning?.points) {
                    const homePoints = inning.points.home || 0;
                    const awayPoints = inning.points.away || 0;
                    inningResults.push(`${homePoints}:${awayPoints}`);

                    totalHome += homePoints;
                    totalAway += awayPoints;
                } else if (inning?.runs) {
                    // Fallback for legacy 'runs' data
                    const homeRuns = inning.runs.home || 0;
                    const awayRuns = inning.runs.away || 0;
                    inningResults.push(`${homeRuns}:${awayRuns}`);

                    totalHome += homeRuns;
                    totalAway += awayRuns;
                } else if (
                    inning &&
                    inning.home !== undefined &&
                    inning.away !== undefined
                ) {
                    inningResults.push(`${inning.home}:${inning.away}`);

                    totalHome += inning.home || 0;
                    totalAway += inning.away || 0;
                } else {
                    inningResults.push('-:-');
                }
            }

            if (
                inningResults.length > 0 &&
                !inningResults.every((p) => p === '-:-')
            ) {
                details = inningResults.join(' | ');

                if (!score && (totalHome > 0 || totalAway > 0)) {
                    score = `${totalHome} : ${totalAway}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format American football result
     * Score: "28 : 21" (total points)
     * Result: "7:3 | 7:7 | 7:7 | 7:4" (points per quarter)
     */
    private formatAmericanFootballResult(
        scoreData: any,
        resultData: any
    ): FormatResult {
        let score = null;
        let details = null;

        // Settlement service uses 'points' indicator
        if (scoreData?.points) {
            score = `${scoreData.points.home || 0} : ${scoreData.points.away || 0}`;
        } else if (
            scoreData?.home !== undefined &&
            scoreData?.away !== undefined
        ) {
            score = `${scoreData.home} : ${scoreData.away}`;
        }

        if (resultData) {
            const quarterResults: string[] = [];
            let totalHome = 0;
            let totalAway = 0;

            // Settlement service uses quarter1-4 phase names
            ['quarter1', 'quarter2', 'quarter3', 'quarter4'].forEach(
                (quarter) => {
                    const q = resultData[quarter];
                    if (q?.points) {
                        quarterResults.push(
                            `${q.points.home || 0}:${q.points.away || 0}`
                        );
                        totalHome += q.points.home || 0;
                        totalAway += q.points.away || 0;
                    } else if (
                        q &&
                        q.home !== undefined &&
                        q.away !== undefined
                    ) {
                        quarterResults.push(`${q.home}:${q.away}`);
                        totalHome += q.home || 0;
                        totalAway += q.away || 0;
                    } else {
                        quarterResults.push('-:-');
                    }
                }
            );

            // Process overtime if present
            const overtime = resultData.overtime;
            if (overtime?.points) {
                quarterResults.push(
                    `OT ${overtime.points.home || 0}:${overtime.points.away || 0}`
                );
                totalHome += overtime.points.home || 0;
                totalAway += overtime.points.away || 0;
            } else if (
                overtime &&
                overtime.home !== undefined &&
                overtime.away !== undefined
            ) {
                quarterResults.push(`OT ${overtime.home}:${overtime.away}`);
                totalHome += overtime.home || 0;
                totalAway += overtime.away || 0;
            }

            if (
                quarterResults.length > 0 &&
                !quarterResults.every((p) => p === '-:-')
            ) {
                details = quarterResults.join(' | ');
                if (!score && (totalHome > 0 || totalAway > 0)) {
                    score = `${totalHome} : ${totalAway}`;
                }
            }
        }

        if (!score) {
            score = '0 : 0';
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format snooker result
     * Score: "5 : 3" (frames won)
     * Result: "75:45 | 60:55 | ..." (points per frame)
     */
    private formatSnookerResult(scoreData: any, resultData: any): FormatResult {
        let score = null;
        let details = null;

        if (scoreData?.frames) {
            score = `${scoreData.frames.home || 0} : ${scoreData.frames.away || 0}`;
        }

        if (resultData) {
            const frameResults: string[] = [];
            let homeFrames = 0;
            let awayFrames = 0;

            // Settlement service uses frame1-15 phase names
            for (let i = 1; i <= 15; i++) {
                const frameKey = `frame${i}`;
                const frame = resultData[frameKey];

                if (frame?.points) {
                    const homePoints = frame.points.home || 0;
                    const awayPoints = frame.points.away || 0;
                    frameResults.push(`${homePoints}:${awayPoints}`);

                    if (homePoints > awayPoints) {
                        homeFrames++;
                    } else if (awayPoints > homePoints) {
                        awayFrames++;
                    }
                }
            }

            if (frameResults.length > 0) {
                details = frameResults.join(' | ');

                if (!score) {
                    score = `${homeFrames} : ${awayFrames}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format rugby result
     * Score: "28 : 21" (total points)
     * Result: "14:10 | 14:11 | OT 3:2" (points per half + optional overtime)
     */
    private formatRugbyResult(scoreData: any, resultData: any): FormatResult {
        let score = null;
        let details = null;

        if (scoreData?.points) {
            score = `${scoreData.points.home || 0} : ${scoreData.points.away || 0}`;
        } else if (
            scoreData?.home !== undefined &&
            scoreData?.away !== undefined
        ) {
            score = `${scoreData.home} : ${scoreData.away}`;
        }

        if (resultData) {
            const halfResults: string[] = [];
            let totalHome = 0;
            let totalAway = 0;

            ['1st_half', '2nd_half'].forEach((half) => {
                const h = resultData[half];
                if (h?.points) {
                    halfResults.push(
                        `${h.points.home || 0}:${h.points.away || 0}`
                    );
                    totalHome += h.points.home || 0;
                    totalAway += h.points.away || 0;
                } else if (h && h.home !== undefined && h.away !== undefined) {
                    halfResults.push(`${h.home}:${h.away}`);
                    totalHome += h.home || 0;
                    totalAway += h.away || 0;
                } else {
                    halfResults.push('-:-');
                }
            });

            // Process overtime if present
            const overtime = resultData.overtime;
            if (overtime?.points) {
                halfResults.push(
                    `OT ${overtime.points.home || 0}:${overtime.points.away || 0}`
                );
                totalHome += overtime.points.home || 0;
                totalAway += overtime.points.away || 0;
            } else if (
                overtime &&
                overtime.home !== undefined &&
                overtime.away !== undefined
            ) {
                halfResults.push(`OT ${overtime.home}:${overtime.away}`);
                totalHome += overtime.home || 0;
                totalAway += overtime.away || 0;
            }

            if (
                halfResults.length > 0 &&
                !halfResults.every((h) => h === '-:-')
            ) {
                details = halfResults.join(' | ');

                if (!score && (totalHome > 0 || totalAway > 0)) {
                    score = `${totalHome} : ${totalAway}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format boxing result (winner-only)
     */
    private formatBoxingResult(scoreData: any, resultData: any): FormatResult {
        let score = null;

        if (scoreData?.winner) {
            score = `${scoreData.winner.home} : ${scoreData.winner.away}`;
        }

        return { score, result: null, goals: null };
    }

    /**
     * Format waterpolo result
     * Score: "12 : 10" (total goals)
     * Result: "3:2 | 3:3 | 3:2 | 3:3" (goals per quarter)
     */
    private formatWaterpoloResult(
        scoreData: any,
        resultData: any
    ): FormatResult {
        let score = null;
        let details = null;

        if (scoreData?.goals) {
            score = `${scoreData.goals.home || 0} : ${scoreData.goals.away || 0}`;
        } else if (
            scoreData?.home !== undefined &&
            scoreData?.away !== undefined
        ) {
            score = `${scoreData.home} : ${scoreData.away}`;
        }

        if (resultData) {
            const quarterResults: string[] = [];
            let totalHome = 0;
            let totalAway = 0;

            ['quarter1', 'quarter2', 'quarter3', 'quarter4'].forEach(
                (quarter) => {
                    const q = resultData[quarter];
                    if (q?.goals) {
                        quarterResults.push(
                            `${q.goals.home || 0}:${q.goals.away || 0}`
                        );
                        totalHome += q.goals.home || 0;
                        totalAway += q.goals.away || 0;
                    } else if (
                        q &&
                        q.home !== undefined &&
                        q.away !== undefined
                    ) {
                        quarterResults.push(`${q.home}:${q.away}`);
                        totalHome += q.home || 0;
                        totalAway += q.away || 0;
                    } else {
                        quarterResults.push('-:-');
                    }
                }
            );

            if (
                quarterResults.length > 0 &&
                !quarterResults.every((q) => q === '-:-')
            ) {
                details = quarterResults.join(' | ');

                if (!score && (totalHome > 0 || totalAway > 0)) {
                    score = `${totalHome} : ${totalAway}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format futsal result (same as football)
     * Score: "5 : 3" (total goals)
     * Result: "3:2 | 2:1" (goals per half)
     */
    private formatFutsalResult(scoreData: any, resultData: any): FormatResult {
        return this.formatFootballResult(scoreData, resultData);
    }

    /**
     * Format esports result
     * Score: "2 : 1" (maps won)
     * Result: "16:14 | 12:16 | 16:10" (points per map)
     */
    private formatEsportsResult(scoreData: any, resultData: any): FormatResult {
        let score = null;
        let details = null;

        if (scoreData?.maps) {
            score = `${scoreData.maps.home || 0} : ${scoreData.maps.away || 0}`;
        }

        if (resultData) {
            const mapResults: string[] = [];
            let homeMaps = 0;
            let awayMaps = 0;

            // Settlement service uses map1-5 phase names
            for (let i = 1; i <= 5; i++) {
                const mapKey = `map${i}`;
                const map = resultData[mapKey];

                if (map?.points) {
                    const homePoints = map.points.home || 0;
                    const awayPoints = map.points.away || 0;
                    mapResults.push(`${homePoints}:${awayPoints}`);

                    if (homePoints > awayPoints) {
                        homeMaps++;
                    } else if (awayPoints > homePoints) {
                        awayMaps++;
                    }
                }
            }

            if (mapResults.length > 0) {
                details = mapResults.join(' | ');

                if (!score) {
                    score = `${homeMaps} : ${awayMaps}`;
                }
            }
        }

        return { score, result: details, goals: null };
    }

    /**
     * Format cricket result (winner-only)
     */
    private formatCricketResult(scoreData: any, resultData: any): FormatResult {
        let score = null;

        if (scoreData?.runs) {
            score = `${scoreData.runs.home || 0} : ${scoreData.runs.away || 0}`;
        } else if (scoreData?.points) {
            score = `${scoreData.points.home || 0} : ${scoreData.points.away || 0}`;
        } else if (
            scoreData?.home !== undefined &&
            scoreData?.away !== undefined
        ) {
            score = `${scoreData.home} : ${scoreData.away}`;
        }

        return { score, result: null, goals: null };
    }

    /**
     * Map sport code to enum
     */
    private normalizeSportCode(sport: string): Sport {
        if (!sport) return Sport.UNKNOWN;

        const upperSport = sport.toUpperCase();
        if (upperSport === 'F' || upperSport === 'FOOTBALL')
            return Sport.FOOTBALL;
        if (upperSport === 'B' || upperSport === 'BASKETBALL')
            return Sport.BASKETBALL;
        if (upperSport === 'H' || upperSport === 'HANDBALL')
            return Sport.HANDBALL;
        if (upperSport === 'T' || upperSport === 'TENNIS') return Sport.TENNIS;
        if (upperSport === 'TT' || upperSport === 'TABLE_TENNIS')
            return Sport.TABLE_TENNIS;
        if (upperSport === 'V' || upperSport === 'VOLLEYBALL')
            return Sport.VOLLEYBALL;
        if (
            upperSport === 'IH' ||
            upperSport === 'ICE_HOCKEY' ||
            upperSport === 'ICEHOCKEY'
        )
            return Sport.ICE_HOCKEY;
        if (upperSport === 'MMA') return Sport.MMA;
        if (upperSport === 'DARTS') return Sport.DARTS;
        if (upperSport === 'BADMINTON') return Sport.BADMINTON;
        if (upperSport === 'BASEBALL') return Sport.BASEBALL;
        if (upperSport === 'AMERICAN_FOOTBALL') return Sport.AMERICAN_FOOTBALL;
        if (upperSport === 'SNOOKER') return Sport.SNOOKER;
        if (upperSport === 'RUGBY') return Sport.RUGBY;
        if (upperSport === 'RUGBY_UNION') return Sport.RUGBY_UNION;
        if (upperSport === 'BOXING') return Sport.BOXING;
        if (upperSport === 'WATERPOLO') return Sport.WATERPOLO;
        if (upperSport === 'FUTSAL') return Sport.FUTSAL;
        if (upperSport === 'ESPORTS') return Sport.ESPORTS;
        if (upperSport === 'CRICKET') return Sport.CRICKET;
        if (upperSport === 'BASKETBALL_3X3' || upperSport === 'BASKETBALL3X3')
            return Sport.BASKETBALL_3X3;
        if (upperSport === 'BASKETBALL_2X20' || upperSport === 'BASKETBALL2X20')
            return Sport.BASKETBALL_2X20;
        return Sport.UNKNOWN;
    }
}
